import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';

import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useSeasonalTooltipFormatter from '@/components/Chart/hooks/tooltip/useSeasonalTooltipFormatter';
import { createChartAppearance } from '@/components/Chart/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import { withSeasonalSeriesAppearance } from '@/components/Chart/utils/applyChartSeriesAppearance';
import { getDayMonthOfYear } from '@/components/Chart/utils/getDayMonthOfYear';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';

import useForwardCurveOptions from './useForwardCurveOptions';

import type { XAxisOptions } from 'highcharts';

const X_AXIS_TICK_INTERVAL = 31;
const X_AXIS_MIN = -5;

/**
 * Configures X-axis options for seasonal charts.
 * Sets tick interval, min value, and month-based label formatting.
 */
const configureSeasonalXAxis = (
  xAxisOptions: XAxisOptions,
  formatDateString: (date: string, variant: DateTextVariants) => string
): void => {
  xAxisOptions.tickInterval = X_AXIS_TICK_INTERVAL;
  xAxisOptions.min = X_AXIS_MIN;

  if (xAxisOptions.labels) {
    xAxisOptions.labels.formatter = function (): string {
      const value = Number(this.value);
      return formatDateString(
        getDayMonthOfYear({ dayOfYear: value + 1 }).toJSON(),
        DateTextVariants.MONTH
      );
    };
  }
};

const useSeasonalOptions = (height?: number) => {
  const { createOptions: createFvCurveOptions } = useForwardCurveOptions(height, true);
  const { createTooltipFormatter } = useSeasonalTooltipFormatter();
  const { formatDateString } = useDateText();
  const theme = useTheme();

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, theme).tooltip,
    [createTooltipFormatter, theme]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const appearance = createChartAppearance(theme, 'regular');
      const coloredSeries = chartSeries.map(series =>
        withSeasonalSeriesAppearance(series, appearance)
      );

      const baseOptions = createFvCurveOptions(coloredSeries, hiddenSeriesIds);
      const xAxisOptions = baseOptions.xAxis as XAxisOptions | undefined;

      if (xAxisOptions) {
        configureSeasonalXAxis(xAxisOptions, formatDateString);
      }

      return {
        ...baseOptions,
        xAxis: xAxisOptions,
        tooltip
      };
    },
    [createFvCurveOptions, formatDateString, theme, tooltip]
  );

  return { createOptions };
};

export default useSeasonalOptions;
